import { ShieldCheck, Mail, Phone, Globe } from "lucide-react";
import { BRAND, IMG } from "../lib/site";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";

const SECTIONS = [
  {
    title: "1. Information We Collect",
    body: [
      "We collect several types of information for various purposes to provide and improve our real estate services to you:",
      "Personal Identifiable Information: While using our website or services, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you, including: Full Name, Email Address (estateparadise81@gmail.com domain interactions), Phone Number / WhatsApp Contact, and property preferences, location interests, and inquiry details.",
      "Log Data: Like many other websites, Estate Paradise makes use of log files. The information inside the log files includes internet protocol (IP) addresses, browser type, Internet Service Provider (ISP), date/time stamp, referring/exit pages, and possibly the number of clicks.",
      "Cookies and Web Beacons: Estate Paradise uses 'cookies' to store information including visitors' preferences, and the pages on the website that the visitor accessed or visited. The information is used to optimize the users' experience by customizing our web page content based on visitors' browser type and/or other information.",
    ],
  },
  {
    title: "2. How We Use Your Information",
    body: [
      "We use the information we collect in various ways, including to:",
      "Provide, operate, and maintain our website and real estate listings.",
      "Process property inquiries, schedule site visits, and respond to your requests.",
      "Communicate with you, either directly or through one of our partners, including for customer service, to provide you with updates and other information relating to properties, and for marketing and promotional purposes.",
      "Send you emails, SMS, or WhatsApp communications regarding property listings and offers.",
      "Find and prevent fraud.",
    ],
  },
  {
    title: "3. Third-Party Privacy Policies & Advertising",
    body: [
      "Estate Paradise's Privacy Policy does not apply to other advertisers or websites. Thus, we are advising you to consult the respective Privacy Policies of these third-party ad servers or services (such as Google Analytics or advertising networks) for more detailed information.",
      "You can choose to disable cookies through your individual browser options. Detailed information about cookie management with specific web browsers can be found at the browsers' respective websites.",
    ],
  },
  {
    title: "4. Protection of Information",
    body: [
      "We adopt appropriate data collection, storage, and processing practices and security measures to protect against unauthorized access, alteration, disclosure, or destruction of your personal information, contact details, and transaction data stored on our site.",
    ],
  },
  {
    title: "5. Information Sharing and Disclosure",
    body: [
      "We do not sell, trade, or rent users' personal identification information to others. We may share generic aggregated demographic information not linked to any personal identification information regarding visitors and users with our trusted business partners, trusted affiliates, and advertisers for the purposes outlined above.",
    ],
  },
  {
    title: "6. Indian Data Protection Compliance",
    body: [
      "For users accessing our website from India, we comply with applicable Indian privacy laws, including the Information Technology Act, 2000 and the Digital Personal Data Protection (DPDP) Act. You reserve the right to access, modify, or request deletion of your personal data collected by us at any time by contacting us directly.",
    ],
  },
  {
    title: "7. Children's Information",
    body: [
      "Another part of our priority is adding protection for children while using the internet. We encourage parents and guardians to observe, participate in, and/or monitor and guide their online activity.",
      "Estate Paradise does not knowingly collect any Personal Identifiable Information from children under the age of 18. If you think that your child provided this kind of information on our website, we strongly encourage you to contact us immediately and we will do our best efforts to promptly remove such information from our records.",
    ],
  },
  {
    title: "8. Changes to This Privacy Policy",
    body: [
      "We may update our Privacy Policy from time to time. Thus, we advise you to review this page periodically for any changes. We will notify you of any changes by posting the new Privacy Policy on this page. These changes are effective immediately after they are posted on this page.",
    ],
  },
];

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <PageHero
        overline="Legal"
        title="Privacy"
        accent="Policy."
        description="How Estate Paradise collects, uses and protects your information."
        image={IMG.arch}
      />

      <section className="bg-white py-20 sm:py-24" data-testid="privacy-content">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 border border-gold-500/40 bg-cream px-6 py-5">
              <span className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-navy-900">
                <ShieldCheck className="h-4 w-4 text-gold-600" />
                Effective Date: November 2, 2026
              </span>
              <span className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-navy-900">
                <Globe className="h-4 w-4 text-gold-600" />
                estateparadise.in
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-10 text-base leading-relaxed text-gray-600">
              At Estate Paradise, accessible from estateparadise.in, one of our main priorities is the privacy of our
              visitors. This Privacy Policy document outlines the types of information that is collected and recorded by
              Estate Paradise and how we use it.
            </p>
            <p className="mt-4 text-base leading-relaxed text-gray-600">
              If you have additional questions or require more information about our Privacy Policy, do not hesitate to
              contact us.
            </p>
          </Reveal>

          <div className="mt-14 space-y-12">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.title} delay={0.05}>
                <section data-testid={`privacy-section-${i + 1}`}>
                  <h2 className="font-serif text-xl font-medium text-navy-900 sm:text-2xl">{s.title}</h2>
                  <span className="mt-3 block h-0.5 w-10 bg-gold-500" aria-hidden="true" />
                  <div className="mt-5 space-y-4">
                    {s.body.map((p, j) => (
                      <p key={j} className="text-[15px] leading-relaxed text-gray-600">
                        {p}
                      </p>
                    ))}
                  </div>
                </section>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.05}>
            <section className="mt-14 border border-navy-900/10 bg-navy-900 p-8" data-testid="privacy-section-9">
              <h2 className="font-serif text-xl font-medium text-white sm:text-2xl">9. Contact Us</h2>
              <span className="mt-3 block h-0.5 w-10 bg-gold-500" aria-hidden="true" />
              <p className="mt-5 text-[15px] leading-relaxed text-white/70">
                If you have any questions or suggestions about our Privacy Policy, or wish to exercise your data rights,
                do not hesitate to contact us:
              </p>
              <div className="mt-6 space-y-3 text-[15px]">
                <p className="text-white/80">
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold-400">Business&nbsp;&nbsp;</span>
                  Estate Paradise
                </p>
                <p className="text-white/80">
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold-400">Website&nbsp;&nbsp;</span>
                  estateparadise.in
                </p>
                <p className="flex items-center gap-2 text-white/80">
                  <Mail className="h-4 w-4 text-gold-400" />
                  <a href={`mailto:${BRAND.email}`} className="transition-colors duration-300 hover:text-gold-400" data-testid="privacy-email-link">
                    {BRAND.email}
                  </a>
                </p>
                <p className="flex items-center gap-2 text-white/80">
                  <Phone className="h-4 w-4 text-gold-400" />
                  <a href={`tel:+${BRAND.phoneRaw}`} className="transition-colors duration-300 hover:text-gold-400" data-testid="privacy-phone-link">
                    {BRAND.phone}
                  </a>
                </p>
              </div>
            </section>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
